import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { motion, Variants } from "framer-motion";
import { createSessionClient } from "../api/client";
import { BallotCandidate, BallotView, CurationStepView, ParticipantSessionView } from "../types";
import Button from "../components/ui/Button";
import EventDetailsModal from "../components/EventDetailsModal";
import AddToCalendar from "../components/AddToCalendar";
import RatePlan from "../components/RatePlan";
import MemberPreferences from "../components/MemberPreferences";
import EnableNotifications from "../components/EnableNotifications";
import PhotoGallery from "../components/PhotoGallery";
import { CLAIM_TOKEN_KEY } from "../components/ClaimHandler";

const listV: Variants = { show: { transition: { staggerChildren: 0.06 } } };
const itemV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

const Spinner = ({ big = false }: { big?: boolean }) => (
  <div className={`flex justify-center ${big ? "py-24" : "py-8"}`}>
    <div
      className={`animate-spin rounded-full border-b-2 border-primary-500 ${big ? "h-12 w-12" : "h-8 w-8"}`}
    />
  </div>
);

const optionCardClass = (on: boolean) =>
  `w-full text-left rounded-2xl border p-3.5 transition-all ${
    on ? "border-primary-500 bg-primary-50 shadow-warm" : "border-primary-100 bg-white/60 hover:border-primary-300"
  }`;

/**
 * Public magic-link page (/s/:token). A family member opens this from their SMS
 * link — no account needed — and says whether they're in for tonight.
 */
const SessionPage: React.FC = () => {
  const { token = "" } = useParams();
  const [view, setView] = useState<ParticipantSessionView | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const getPhotoClient = useMemo(() => {
    const client = createSessionClient(token);
    return async () => client;
  }, [token]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await createSessionClient(token).get<ParticipantSessionView>("/api/session");
      setView(data);
      setInvalid(false);
    } catch {
      setInvalid(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const postOptIn = async (optedIn: boolean, latitude: number | null, longitude: number | null) => {
    setSaving(true);
    try {
      const { data } = await createSessionClient(token).post<ParticipantSessionView>("/api/session/optin", {
        optedIn,
        latitude,
        longitude,
      });
      setView(data);
    } catch {
      setInvalid(true);
    } finally {
      setSaving(false);
    }
  };

  const setOptIn = (optedIn: boolean) => {
    // Ask for location only when joining; if denied/unavailable, join anyway.
    if (optedIn && "geolocation" in navigator) {
      setSaving(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => postOptIn(true, pos.coords.latitude, pos.coords.longitude),
        () => postOptIn(true, null, null),
        { timeout: 8000, maximumAge: 60000 }
      );
    } else {
      postOptIn(optedIn, null, null);
    }
  };

  if (loading) return <Spinner big />;

  if (invalid || !view) {
    return (
      <div className="mx-auto mt-16 max-w-md text-center">
        <div className="text-5xl">⌛</div>
        <h1 className="mt-4 font-display text-2xl font-semibold text-ink">This link isn't valid anymore</h1>
        <p className="mt-2 text-ink/65">
          Magic links expire after a while. Ask whoever started tonight's plan to send a fresh one.
        </p>
      </div>
    );
  }

  const optedIn = view.optInStatus === "OptedIn";
  const declined = view.optInStatus === "Declined";

  return (
    <motion.div
      className="mx-auto mt-8 max-w-md"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="rounded-4xl border border-primary-100 bg-white/80 p-7 text-center shadow-warm backdrop-blur-sm">
        <img src="/logo.svg" alt="Kinoes" className="mx-auto h-14 w-14" />
        <h1 className="mt-3 font-display text-3xl font-semibold text-ink">Hey {view.memberDisplayName}!</h1>
        <p className="mt-2 text-ink/65">
          {view.familyName} is deciding what to do{" "}
          {isToday(view.targetDate) ? "tonight" : `on ${view.targetDate}`}. Are you in?
        </p>

        {optedIn ? (
          <div className="mt-6">
            <div className="rounded-2xl bg-primary-50 px-4 py-3 font-medium text-primary-700 ring-1 ring-primary-100">
              You're in! 🎉 Help us pick:
            </div>
            <CurationStep token={token} sessionState={view.state} />
            <EnableNotifications token={token} />
            <PhotoGallery getClient={getPhotoClient} basePath="/api/session" />
            <MemberPreferences token={token} />
            <button
              onClick={() => setOptIn(false)}
              disabled={saving}
              className="mt-4 text-sm text-ink/45 underline hover:text-ink/70"
            >
              Actually, I can't make it
            </button>
            <AccountPrompt token={token} />
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <Button fullWidth size="lg" onClick={() => setOptIn(true)} disabled={saving} className="text-lg">
              I'm in tonight
            </Button>
            <Button variant="secondary" fullWidth onClick={() => setOptIn(false)} disabled={saving}>
              Can't make it
            </Button>
            {declined && (
              <p className="text-sm text-ink/50">No worries — tap "I'm in" if that changes.</p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const MOODS: { value: string; label: string; icon: string }[] = [
  { value: "new", label: "Something new", icon: "✨" },
  { value: "reliable", label: "A reliable favorite", icon: "💛" },
  { value: "either", label: "Either's good", icon: "🤷" },
];

/** After opting in, the member answers AI curation questions here. */
const CurationStep: React.FC<{ token: string; sessionState: string }> = ({ token, sessionState }) => {
  const inCuration = ["GatheringParticipants", "IndividualCuration", "ConsensusBuilding"].includes(sessionState);
  const [phase, setPhase] = useState<"intake" | "curate">(inCuration ? "intake" : "curate");
  const [mood, setMood] = useState<string | null>(null);
  const [specific, setSpecific] = useState("");
  const [step, setStep] = useState<CurationStepView | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await createSessionClient(token).get<CurationStepView>("/api/session/curation");
    setStep(data);
    setSelected([]);
  }, [token]);

  // Only fetch (which generates the AI question) once intent is captured.
  useEffect(() => {
    if (phase === "curate") load();
  }, [phase, load]);

  const submitIntake = async () => {
    setBusy(true);
    try {
      await createSessionClient(token).post("/api/session/curation/intake", {
        mood,
        specific: specific.trim() || null,
      });
      setPhase("curate");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    if (!step?.turn) return;
    setBusy(true);
    try {
      const { data } = await createSessionClient(token).post<CurationStepView>("/api/session/curation/answer", {
        turnId: step.turn.turnId,
        selectedTempIds: selected,
        sentiment: "Liked",
      });
      setStep(data);
      setSelected([]);
    } finally {
      setBusy(false);
    }
  };

  // "None of these" — ask for a fresh set of options instead of submitting picks.
  const more = async () => {
    if (!step?.turn) return;
    setBusy(true);
    try {
      const { data } = await createSessionClient(token).post<CurationStepView>("/api/session/curation/more", {
        turnId: step.turn.turnId,
      });
      setStep(data);
      setSelected([]);
    } finally {
      setBusy(false);
    }
  };

  if (phase === "intake") {
    return (
      <div className="mt-5 text-left">
        <p className="font-medium text-ink">What are you up for tonight?</p>
        <div className="mt-3 grid grid-cols-1 gap-2">
          {MOODS.map((m) => (
            <button
              key={m.value}
              onClick={() => setMood(m.value)}
              className={`rounded-2xl border p-3 text-left transition-all ${
                mood === m.value
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-primary-100 bg-white/60 hover:border-primary-300"
              }`}
            >
              <span className="mr-2">{m.icon}</span>
              <span className="font-medium">{m.label}</span>
            </button>
          ))}
        </div>
        <input
          value={specific}
          onChange={(e) => setSpecific(e.target.value)}
          placeholder="Anything specific in mind? (a craving, a place, an idea)"
          className="mt-3 w-full rounded-xl border border-primary-200 bg-white/70 px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
        <Button fullWidth size="lg" className="mt-4" onClick={submitIntake} disabled={busy}>
          {busy ? "…" : "Continue"}
        </Button>
      </div>
    );
  }

  if (!step) return <Spinner />;
  if (step.complete) return <VotingStep token={token} />;

  const turn = step.turn!;
  return (
    <div className="mt-5 text-left">
      <p className="font-medium text-ink">{turn.promptText}</p>
      <motion.div className="mt-3 space-y-2" variants={listV} initial="hidden" animate="show">
        {turn.options.map((o) => {
          const on = selected.includes(o.tempId);
          return (
            <motion.button key={o.tempId} variants={itemV} onClick={() => toggle(o.tempId)} className={optionCardClass(on)}>
              <div className="flex items-center justify-between">
                <span className="font-display font-semibold text-ink">{o.title}</span>
                <span className="text-xs uppercase tracking-wide text-primary-400">{o.category}</span>
              </div>
              <p className="mt-1 text-sm text-ink/65">{o.description}</p>
            </motion.button>
          );
        })}
      </motion.div>
      <Button fullWidth size="lg" className="mt-4" onClick={selected.length > 0 ? submit : more} disabled={busy}>
        {busy ? "Sending…" : selected.length > 0 ? "Send my picks" : "None of these — show more"}
      </Button>
    </div>
  );
};

/** After curating, members vote on the final options and see the decision. */
const VotingStep: React.FC<{ token: string }> = ({ token }) => {
  const [ballot, setBallot] = useState<BallotView | null>(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<BallotCandidate | null>(null);

  const load = useCallback(async () => {
    const { data } = await createSessionClient(token).get<BallotView>("/api/session/vote");
    setBallot(data);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll so members see others' votes and the final decision land.
  useEffect(() => {
    if (ballot?.decision) return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [ballot?.decision, load]);

  const vote = async (suggestionId: string) => {
    setBusy(true);
    try {
      const { data } = await createSessionClient(token).post<BallotView>("/api/session/vote", { suggestionId });
      setBallot(data);
    } finally {
      setBusy(false);
    }
  };

  if (!ballot) return <Spinner />;

  if (ballot.decision) {
    return (
      <motion.div
        className="mt-5"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
      >
        <div className="rounded-3xl bg-gradient-to-br from-primary-500 to-primary-700 p-6 text-left text-white shadow-glow">
          <div className="text-xs uppercase tracking-widest opacity-80">🎬 Tonight's plan</div>
          <div className="mt-1.5 font-display text-2xl font-semibold leading-tight">
            {ballot.decision.winningTitle}
          </div>
          <AddToCalendar
            title={ballot.decision.winningTitle}
            startsAt={ballot.decision.startsAt}
            location={ballot.candidates.find((c) => c.id === ballot.decision!.winningSuggestionId)?.title}
            url={ballot.candidates.find((c) => c.id === ballot.decision!.winningSuggestionId)?.url}
          />
        </div>
        {ballot.decision.rationale && (
          <p className="mt-3 text-sm italic text-ink/65">"{ballot.decision.rationale}"</p>
        )}
        <p className="mt-2 text-xs text-ink/45">Decided by {prettyMethod(ballot.decision.method)}.</p>
        <RatePlan
          startsAt={ballot.decision.startsAt}
          yourRating={ballot.yourRating}
          average={ballot.averageRating}
          count={ballot.ratingCount}
          onRate={async (stars, note) => {
            const { data } = await createSessionClient(token).post<BallotView>("/api/session/rating", { stars, note });
            setBallot(data);
          }}
        />
      </motion.div>
    );
  }

  if (ballot.candidates.length === 0) {
    return (
      <div className="mt-5 text-sm text-ink/65">
        Thanks! Lining up tonight's options
        {ballot.sessionState === "IndividualCuration" ? " — waiting for the family to finish" : ""}…
      </div>
    );
  }

  return (
    <div className="mt-5 text-left">
      <p className="font-medium text-ink">Vote for tonight:</p>
      <motion.div className="mt-3 space-y-2" variants={listV} initial="hidden" animate="show">
        {ballot.candidates.map((c) => {
          const mine = ballot.yourVoteSuggestionIds.includes(c.id);
          const voters = ballot.participants.filter((p) => p.votedSuggestionIds.includes(c.id)).map((p) => p.displayName);
          return (
            <motion.div key={c.id} variants={itemV}>
              <button
                onClick={() => vote(c.id)}
                disabled={busy}
                className={`${optionCardClass(mine)} disabled:opacity-60`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-semibold text-ink">{c.title}</span>
                  <span className="text-xs text-ink/45">
                    {c.voteCount} vote{c.voteCount === 1 ? "" : "s"}
                  </span>
                </div>
                <p className="mt-1 text-sm text-ink/65">{c.description}</p>
                {c.warning && <p className="mt-1 text-xs font-medium text-amber-700">⚠ {c.warning}</p>}
                {voters.length > 0 && <p className="mt-1 text-xs text-ink/50">{voters.join(", ")}</p>}
                {mine && <p className="mt-1 text-xs font-medium text-primary-700">Your pick</p>}
              </button>
              {(c.url || (c.latitude != null && c.longitude != null)) && (
                <button
                  onClick={() => setDetail(c)}
                  className="ml-1 mt-1 inline-block text-xs font-medium text-dusk-500 hover:text-dusk-700"
                >
                  View details
                </button>
              )}
            </motion.div>
          );
        })}
      </motion.div>
      <p className="mt-3 text-xs text-ink/45">Tap up to 2 — the organizer locks in the final plan.</p>
      <Roster participants={ballot.participants} />
      <EventDetailsModal candidate={detail} onClose={() => setDetail(null)} />
    </div>
  );
};

/** Live "who's in / who's weighed in" roster shown to participants. */
const Roster: React.FC<{ participants: BallotView["participants"] }> = ({ participants }) => {
  if (participants.length === 0) return null;
  const statusOf = (p: BallotView["participants"][number]) =>
    p.votedSuggestionIds.length > 0 ? "voted ✓" : p.optInStatus === "Declined" ? "out" : p.included ? "thinking…" : "invited";
  return (
    <div className="mt-5 border-t border-primary-100 pt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">Who's in</p>
      <ul className="mt-1.5 space-y-1">
        {participants.map((p) => (
          <li key={p.memberId} className="text-sm">
            <div className="flex items-center justify-between">
              <span className="text-ink/80">{p.displayName}</span>
              <span className="text-ink/50">{statusOf(p)}</span>
            </div>
            {p.liked.length > 0 && (
              <p className="text-xs text-ink/45">
                <span className="text-primary-500">♥</span> {p.liked.join(", ")}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Invites the member to create an account that links to their family member record. */
const AccountPrompt: React.FC<{ token: string }> = ({ token }) => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  if (isAuthenticated) return null;
  const createAccount = () => {
    localStorage.setItem(CLAIM_TOKEN_KEY, token);
    loginWithRedirect({ authorizationParams: { screen_hint: "signup" } });
  };
  return (
    <div className="mt-5 rounded-2xl border border-primary-100 bg-cream-100 p-4 text-left">
      <p className="font-display font-semibold text-ink">Plan with your family anytime</p>
      <p className="mt-1 text-sm text-ink/65">
        Create a free account to save your spot, follow results, and start your own plans.
      </p>
      <Button className="mt-3" fullWidth onClick={createAccount}>
        Create my account
      </Button>
    </div>
  );
};

function prettyMethod(method: string): string {
  switch (method) {
    case "Unanimous":
      return "unanimous vote";
    case "Majority":
      return "majority vote";
    case "AiTieBreak":
      return "Kinoes (tie-breaker)";
    default:
      return method;
  }
}

function isToday(dateString: string): boolean {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return dateString === `${yyyy}-${mm}-${dd}`;
}

export default SessionPage;
